import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  /**
   * Returns the slideshow element with a select method.
   * @returns {{ select: (input: unknown, event?: Event, options?: { animate?: boolean }) => void } | null}
   */
  #getSlideshowElement() {
    const candidate = this.slideshow ?? this.querySelector('slideshow-component');
    /** @type {any} */
    const candidateWithSelect = candidate;
    if (!candidateWithSelect || typeof candidateWithSelect.select !== 'function') return null;
    return candidateWithSelect;
  }

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    // Listen globally because the variant:update event source can be replaced/morphed
    // and may not always bubble through the gallery's closest section container.
    document.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    /** @type {any} */
    const debugWindow = window;
    const debug = debugWindow.__variantDebug === true;
    const source = event.detail.data.html;
    const eventProductId = event.detail.data.productId;
    const currentProductId = this.dataset.productId;
    const featuredMediaId = event.detail.resource?.featured_media?.id
      ? String(event.detail.resource.featured_media.id)
      : null;
    if (debug) {
      console.debug('[media-gallery] variant:update', {
        eventProductId,
        currentProductId,
        featuredMediaId,
      });
    }

    if (eventProductId && currentProductId && String(eventProductId) !== String(currentProductId)) return;

    // First, try to switch instantly using the current gallery DOM.
    if (featuredMediaId) {
      const slideshow = this.#getSlideshowElement();
      if (debug) {
        console.debug('[media-gallery] select:current', {
          hasSlideshow: Boolean(slideshow),
          featuredMediaId,
        });
      }
      slideshow?.select({ id: featuredMediaId }, undefined, { animate: false });

      // Grid fallback: move selected media container to first position.
      const grid = this.querySelector('.media-gallery__grid');
      if (grid) {
        const item = grid.querySelector(`[data-media-id="${CSS.escape(featuredMediaId)}"]`);
        if (item && item !== grid.firstElementChild) {
          grid.prepend(item);
        }
      }
    }

    // Then keep existing replacement behavior for full consistency (availability, URLs, etc).
    if (!source) return;

    let newMediaGallery = null;
    if (eventProductId) {
      newMediaGallery = source.querySelector(`media-gallery[data-product-id="${CSS.escape(String(eventProductId))}"]`);
    }

    if (!newMediaGallery) newMediaGallery = source.querySelector('media-gallery');
    if (!newMediaGallery) return;
    if (debug) {
      console.debug('[media-gallery] replace:incoming-gallery', {
        foundIncoming: Boolean(newMediaGallery),
        featuredMediaId,
      });
    }

    if (featuredMediaId) {
      /** @type {any} */
      const newSlideshow = newMediaGallery.querySelector('slideshow-component');
      if (newSlideshow && typeof newSlideshow.select === 'function') {
        newSlideshow.select({ id: featuredMediaId }, undefined, { animate: false });
      }

      const newGrid = newMediaGallery.querySelector('.media-gallery__grid');
      if (newGrid) {
        const newItem = newGrid.querySelector(`[data-media-id="${CSS.escape(featuredMediaId)}"]`);
        if (newItem && newItem !== newGrid.firstElementChild) {
          newGrid.prepend(newItem);
        }
      }
    }

    this.replaceWith(newMediaGallery);

    if (featuredMediaId) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const selector = eventProductId
            ? `media-gallery[data-product-id="${CSS.escape(String(eventProductId))}"]`
            : 'media-gallery';
          const mountedGallery = document.querySelector(selector);
          if (!mountedGallery) return;

          /** @type {any} */
          const mountedGalleryAny = mountedGallery;
          /** @type {any} */
          const mountedSlideshow =
            mountedGalleryAny.querySelector('slideshow-component') ?? mountedGalleryAny.slideshow;
          if (mountedSlideshow && typeof mountedSlideshow.select === 'function') {
            mountedSlideshow.select({ id: featuredMediaId }, undefined, { animate: false });
            if (debug) {
              console.debug('[media-gallery] select:mounted', {
                selector,
                featuredMediaId,
                hasMountedSlideshow: true,
              });
            }
          } else if (debug) {
            console.debug('[media-gallery] select:mounted-missing-slideshow', { selector, featuredMediaId });
          }

          const mountedGrid = mountedGallery.querySelector('.media-gallery__grid');
          if (mountedGrid) {
            const mountedItem = mountedGrid.querySelector(`[data-media-id="${CSS.escape(featuredMediaId)}"]`);
            if (mountedItem && mountedItem !== mountedGrid.firstElementChild) {
              mountedGrid.prepend(mountedItem);
            }
          }
        });
      });
    }
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    const slideshow = this.#getSlideshowElement();
    slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
